/** Helpers for narration flow project history panel. */

export function isNarrationFlowProject(project) {
  if (!project?.id) return false;
  const app = (project.app || '').toLowerCase();
  const kind = (project.input?.project_kind || project.project_kind || 'narration').toLowerCase();
  return app !== 'news_video' && kind !== 'news';
}

export function formatNarrationDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function narrationStatusLabel(project) {
  if (!project) return 'Draft';
  if (project.final_video_url) return 'Ready';
  const st = (project.status || '').toLowerCase();
  if (st === 'completed') return 'Ready';
  if (st === 'script_ready') return 'Script ready';
  if (st === 'running' || st === 'pending') return 'Processing';
  if (st === 'failed') return 'Failed';
  const segCount = project.segments?.length || project.input?.source_segments?.length || 0;
  if (segCount > 0) return `${segCount} scenes`;
  return st || 'Draft';
}

export function segmentsToImageStoryScenes(segments) {
  if (!Array.isArray(segments)) return [];
  return segments.map((seg, idx) => ({
    ...seg,
    client_id: seg.client_id || seg.id || `hist-${idx}`,
    sort_order: Number(seg.sort_order ?? idx),
    segment_number: Number(seg.segment_number ?? idx + 1),
    panel_number: Number(seg.panel_number ?? seg.segment_number ?? idx + 1),
    panel_text: seg.panel_text || seg.visual || '',
    visual: seg.visual || seg.panel_text || '',
    text: seg.text || seg.script || '',
    narration_text: seg.narration_text || seg.text || '',
    scene_text: seg.scene_text || seg.text || '',
    image_url: seg.image_url || seg.source_image_url || null,
    source_image_url: seg.source_image_url || seg.image_url || null,
  }));
}

/** @param {object} project @param {object} initialForm */
export function narrationFormFromProject(project, initialForm) {
  const input = project?.input || {};
  return {
    ...initialForm,
    title: project?.title || input.title || '',
    topic: input.topic || '',
    full_script: input.full_script || '',
    tone: input.tone || initialForm.tone,
    visual_style: input.visual_style || initialForm.visual_style,
    segment_count: Number(input.segment_count) || initialForm.segment_count,
    image_provider: input.image_provider || initialForm.image_provider,
    video_provider: input.video_provider || initialForm.video_provider,
    image_aspect_ratio: input.image_aspect_ratio || initialForm.image_aspect_ratio,
    bg_music_provider: input.bg_music_provider || initialForm.bg_music_provider,
    voice_style: input.voice_style || initialForm.voice_style,
    tts_provider: input.tts_provider || initialForm.tts_provider,
    language: input.language || initialForm.language,
    narrator_elevenlabs_voice_id: input.narrator_elevenlabs_voice_id || '',
    narrator_murf_voice_id: input.narrator_murf_voice_id || '',
    narrator_murf_locale: input.narrator_murf_locale || '',
    narrator_minimax_voice_id: input.narrator_minimax_voice_id || '',
    character_ids: Array.isArray(input.character_ids) ? input.character_ids : [],
    include_subtitles: Boolean(input.include_subtitles),
    source_segments: Array.isArray(input.source_segments) ? input.source_segments : [],
    full_automation: Boolean(input.full_automation),
    generate_ai_video_during_pipeline: Boolean(input.generate_ai_video_during_pipeline),
    auto_publish_youtube: Boolean(input.auto_publish_youtube),
    auto_publish_instagram: Boolean(input.auto_publish_instagram),
    youtube_publish_language: input.youtube_publish_language || initialForm.youtube_publish_language,
  };
}

export function narrationWizardStepFromProject(project) {
  if (!project) return 'path';
  if (project.final_video_url || (project.status || '').toLowerCase() === 'completed') {
    return 'generate';
  }
  const scenes = project.segments?.length || project.input?.source_segments?.length || 0;
  if (scenes > 0) return 'visuals';
  return 'path';
}
